import React, { useRef, useState, useCallback } from 'react';
import config from '@plone/volto/registry';
import { FullscreenControl, Marker, NavigationControl } from 'react-map-gl';
import { Map, useMap, Popup, ScaleControl, Source, Layer } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@codesyntax/volto-maplibre-block/components/MapLibre/style.css';
import { FitBounds } from '@codesyntax/volto-maplibre-block/components/MapLibre/MapLibre';
import { basicOSMStyle, calcBoundsFromCoordinates } from '@codesyntax/volto-maplibre-block/components/MapLibre/utils';
import markerPNG from '@codesyntax/volto-maplibre-block/icons/marker-icon.png';
import {clusterLayer, clusterCountLayer, unclusteredPointLayer} from './layers';
import maplibregl from 'maplibre-gl';

export const MapLibreCounter = (props) => {
  const {
    markers = [],
    marker = null,
    center = {
      latitude: 43.2083,
      longitude: -2.4681,
      zoom: 12,
    },
    tilesLayer,
    height = '500px',
    fitBounds = true,
  } = props;

  const selectedMapStyles =
    config.blocks.blocksConfig.mapLibreBlock.tileLayers.filter(
      (item) => item.id === tilesLayer,
    );
  const selectedMapStyle = selectedMapStyles[0];

  const mapStyle = selectedMapStyle
    ? {
        version: 8,
        sources: {
          [selectedMapStyle.id]: {
            type: selectedMapStyle.type,
            tiles: selectedMapStyle.urls,
            tileSize: selectedMapStyle.tileSize,
            attribution: selectedMapStyle.attribution,
            maxzoom: selectedMapStyle.maxzoom,
          },
        },
        layers: [
          {
            id: selectedMapStyle.id,
            type: selectedMapStyle.type,
            source: selectedMapStyle.id,
          },
        ],
      }
    : basicOSMStyle;

  mapStyle.glyphs = "/fonts/{fontstack}/{range}.pbf"  //Add glyph for labels
  // console.info('MapLibre:', 'mapStyle=', mapStyle);

  const geoJSONData = {
    "type": "FeatureCollection",
    "features": [],
  };
  if (markers.length) {
    for (let i = 0; i < markers.length; i++) {
      const item = markers[i];
      const feature = {
        "properties": {
          "id": i,
          "title": item.title,
          "counter": Number(item.counter),
          "popup_text": item.popup_text
        },
        "geometry": {
          "type": "Point",
          "coordinates": [ item.longitude, item.latitude, 0.0 ] }
      }
      geoJSONData.features.push(feature);
    }
  };

  const mapRef = useRef();

  const onMapLoad = useCallback(() => {
    const map = mapRef.current.getMap();
    // console.log('Map loaded', map);

    if (!map) return;

    const showPopup = (lngLat, html) => {
      new maplibregl.Popup()
        .setLngLat(lngLat)
        .setHTML(html)
        .addTo(map);
    };

    const getPopupHTML = (features) => {
      const html = `<div style="text-align: center;">` +
        features
          .map((f) => {
            const text = f.properties.popup_text || 'No text';
            return text
              .split('\n')
              .map(line => `<div>${line}</div>`)
              .join('');
          })
          .join('')
        + `</div>`;

      console.log(html);
      return html;
    };

    const handleClusterClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });

      const cluster = features[0];
      const clusterId = cluster.properties.cluster_id;

      const source = map.getSource('markers');
      if (!source?.getClusterLeaves) return;

      source.getClusterLeaves(clusterId, 10, 0, (err, leaves) => {
        if (err) return;
        const html = getPopupHTML(leaves);
        showPopup(cluster.geometry.coordinates, html);
      });
    };

    const handleUnclusteredClick = (e) => {
      const feature = e.features[0];
      const html = getPopupHTML([feature]);
      showPopup(feature.geometry.coordinates, html);
    };

    const setCursor = (layer) => {
      map.on('mouseenter', layer, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layer, () => {
        map.getCanvas().style.cursor = '';
      });
    };
    // Attach events
    map.on('click', 'clusters', handleClusterClick);
    map.on('click', 'single-point', handleUnclusteredClick);
    setCursor('clusters');
    setCursor('single-point');

  }, []);

  return (
    <div className="map" style={{ height: height }}>
      <Map
          ref={mapRef}
          mapLib={maplibregl}
          initialViewState={center}
          mapStyle={mapStyle}
          onLoad={onMapLoad}
        >
        <FullscreenControl position="top-left" />
        <NavigationControl position="top-left" />
        <ScaleControl />
        <Source
          id="counter-pins"
          type="geojson"
          data={geoJSONData}
          cluster={true}
          clusterMaxZoom={14}
          clusterRadius={50}
          clusterProperties={{
            totalCounter: ['+', ['get', 'counter']] // This aggregates the counters
          }}
        >
          {/* Cluster circles */}
          <Layer
            id="clusters"
            type="circle"
            filter={['has', 'point_count']}
            paint={{
              'circle-radius': 26,
              //'circle-color': 'rgba(161, 19, 14, 0.4)',  // Background
              'circle-color' : [
                'step',
                ['get', 'totalCounter'],
                'rgba(134, 233, 21, 0.5)',
                5,
                'rgba(199, 164, 7, 0.5)',
                10,
                'rgba(230, 13, 6, 0.5)'
              ],
              'circle-stroke-color': 'rgb(255,255,255)', // Border
              'circle-stroke-width': 1,
            }}
          />

          {/* Cluster labels - THIS SHOWS TOTAL COUNTER */}
          <Layer
            id="cluster-labels"
            type="symbol"
            filter={['has', 'point_count']}
            layout={{
              'text-field': [
                'format',
                ['number-format',
                  ['coalesce', ['get', 'totalCounter'], 0],
                  { 'min-fraction-digits': 0, 'max-fraction-digits': 0 }
                ]
              ],
              'text-font': ['Open Sans Bold'],
              'text-size': 16,
              'text-allow-overlap': true,
              'text-justify': 'center',
              'text-anchor': 'center'
            }}
            paint={{
              // 'text-color': 'rgb(255,255,255)', 
              'text-color' : [
                'step',
                ['get', 'totalCounter'],
                'rgb(65, 107, 18)',
                5,
                'rgb(148, 111, 11)',
                10,
                'rgb(131, 19, 15)'
              ],
              'text-halo-color': 'rgba(161, 19, 14, 0.8)',
              'text-halo-width': 0
            }}
          />

          {/* Individual points */}
          <Layer
            id="single-point"
            type="circle"
            filter={['!', ['has', 'point_count']]}
            paint={{
              'circle-radius': 16,
              //'circle-color': 'rgba(161, 19, 14, 0.4)',  // Background
              'circle-color' : [
                'step',
                ['get', 'counter'],
                'rgba(134, 233, 21, 0.8)',
                5,
                'rgba(199, 164, 7, 0.8)',
                10,
                'rgba(230, 13, 6, 0.8)'
              ],
              'circle-stroke-color': 'rgb(255,255,255)', // Border
              'circle-stroke-width': 1,
            }}
          />

          {/* Individual labels */}
          <Layer
            id="single-label"
            type="symbol"
            filter={['!', ['has', 'point_count']]}
            layout={{
              'text-field': [
                'format',
                ['number-format',
                  ['coalesce', ['get', 'counter'], 0],
                  { 'min-fraction-digits': 0, 'max-fraction-digits': 0 }
                ]
              ],
              'text-font': ['Open Sans Bold'],
              'text-size': 16
            }}
            paint={{
              //'text-color': 'rgb(255,255,255)',
              'text-color' : [
                'step',
                ['get', 'counter'],
                'rgb(65, 107, 18)',
                5,
                'rgb(148, 111, 11)',
                10,
                'rgb(131, 19, 15)'
              ],
              'text-halo-color': 'rgba(20, 179, 92, 0.8)',
              'text-halo-width': 0
            }}
          />
        </Source>

        {markers.length > 1 && fitBounds && <FitBounds markers={markers} />}

      </Map>
    </div>
  );
};
